import * as D3 from "d3";
import md from "@/utils/markdown/md";
import { nanoid } from "nanoid";
import { getImageSrcFromHtml } from "@/utils/tool";
import { interpolateRainbow } from "d3-scale-chromatic";
import { getNodeGameStatus } from '@/utils/learning/progressStore';

export const Node = {
  width: 600,
  height: 60,
  r: 8,
  color: "inherit",
};

const TransitionDuration = 500;
const dpr = 1;

export default {
  data() {
    return {
      layoutMode: "lr", // 'center' 中心布局 | 'lr' 从左到右布局
    };
  },
  methods: {

    // ========== ★ 状态点定位 ==========
    // 点放在折叠图标(+/-)正下方：与图标同 x、略低于图标底部，零遮挡
    getDotX(d) {
      if (d.depth === 0) return 0;
      if (this.layoutMode === 'lr') {
        return this.getWidth(d) / 2 + 21;   // 与 toggle 的 x 完全一致
      }
      // center 布局：复制 toggle 的 x 计算逻辑
      const rectWidth = this.getWidth(d);
      const rootX = this.hierarchy?.x ?? 0;
      if (d.x === 0 && d.data.image) return rectWidth / 2 + 75;
      if (d.x === 0) return rectWidth / 2 + 10;
      if (d.x > rootX) {
        return d.data.image ? rectWidth / 2 + 65 : rectWidth / 2 + 3;
      }
      return d.data.image ? -rectWidth / 2 - 80 : -rectWidth / 2 - 22;
    },
    getDotY(d) {
      // 折叠图标 dy=30、字号34px → 图标底部约 y=40；点放其下方，留一点间隙
      return 36;
    },

    // ========== 初始化 ==========
    initGraphSvg() {
      const container = this.getGraphContainer();
      if (!container || !this.currentPaintData) return;

      this.$svg = D3.create("svg");
      this.$svg.attr("width", "100%");
      this.$svg.attr("height", "100%");
      this.$svg.attr("class", "graph-svg");

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const viewBoxX = -containerWidth / 2;
      const viewBoxY = -containerHeight / 2;
      this.$svg
        .attr("viewBox", `${viewBoxX} ${viewBoxY} ${containerWidth} ${containerHeight}`)
        .on("dblclick", () => this.resetView());

      this.$wrap = this.$svg.append("g");

      this.zoom = D3.zoom().scaleExtent([0.5, 4]);
      this.zoom
        .translateExtent([[-Infinity, -Infinity], [Infinity, Infinity]])
        .on("zoom", () => {
          const transform = D3.event.transform;
          this.$wrap.attr("transform", transform);
        })
        .on("start", () => {})
        .on("end", () => {});
      this.$svg.call(this.zoom);

      this.$linkGroup = this.$wrap.append("g").attr("class", "link-group");
      this.$nodeGroup = this.$wrap.append("g").attr("class", "node-group");
      container.appendChild(this.$svg.node());

      this.tree = D3.tree()
        .nodeSize([Node.height * 1.5, Node.width * 0.8])
        .separation(this.getNodeSeparation);

      this.hierarchy = D3.hierarchy(this.currentPaintData);
      this.hierarchy.descendants().forEach((node) => {
        node.id = nanoid();
        if (node.depth >= 1) {
          if (node.children) {
            node.isFold = true;
            node._children = node.children;
            node.children = null;
          }
        }
      });

      this.draw(true);
    },

    // 重建图谱（切换数据源时使用）
    rebuildGraph() {
      const container = this.getGraphContainer();
      if (!container) return;
      container.innerHTML = "";
      this.$svg = null;
      this.$wrap = null;
      this.$linkGroup = null;
      this.$nodeGroup = null;
      this.tree = null;
      this.hierarchy = null;
      this.initGraphSvg();
    },

    // ========== 工具 ==========
    getWidth(d) {
      if (!d || !d.data || !d.data.name) return 0;
      if (d.data.name.length <= 4) {
        if (d.data.name.length === 1) {
          return 60;
        }
        return d.data.name.length * 40 * dpr;
      }
      if (d.data.name.length > 20) {
        return 300 * dpr;
      }
      return d.data.name.length * 20 + 120;
    },

    getNodeSeparation(a, b) {
      let aDescLength = a.data.description ? a.data.description.length : 0;
      let bDescLength = b.data.description ? b.data.description.length : 0;
      const additionalSeparation = Math.max(aDescLength, bDescLength) * 0.1 * dpr;
      const baseSeparation = 1.2;
      if (a.depth === b.depth) {
        return baseSeparation + additionalSeparation / 5;
      }
      return baseSeparation;
    },

    generateConsolidatedPath(d) {
      if (this.layoutMode === "lr") {
        const toggleOffset = d.source.depth > 0 ? 40 : 0;
        const sourceX = d.source.x + this.getWidth(d.source) / 2 + toggleOffset;
        const sourceY = d.source.y + 18;
        const targetX = d.target.x - this.getWidth(d.target) / 2;
        const targetY = d.target.y + 18;
        return `M${sourceX},${sourceY} C${(sourceX + targetX) / 2},${sourceY} ${(sourceX + targetX) / 2},${targetY} ${targetX},${targetY}`;
      } else {
        let sourceX = d.source.x;
        if (sourceX < 0) {
          sourceX = sourceX - this.getWidth(d.source) / 2 - 30;
        } else if (sourceX > 0) {
          sourceX = sourceX + this.getWidth(d.source) / 2 + 30;
        }
        const sourceY = d.source.y + 18;
        let targetX = d.target.x;
        if (targetX > 0) {
          if (d.source.data.image) {
            sourceX = sourceX + 55;
            if (sourceX === 55) {
              sourceX += this.getWidth(d.source) / 2;
            }
          }
        } else if (targetX < 0) {
          if (d.source.data.image) {
            if (sourceX === 0) {
            } else {
              sourceX = sourceX - 55;
            }
          }
        }
        const targetY = d.target.y + 20;
        if (sourceX > targetX) {
          return `M${targetX},${targetY} C${(targetX + sourceX) / 2},${targetY} ${(targetX + sourceX) / 2},${sourceY} ${sourceX},${sourceY}`;
        }
        return `M${sourceX},${sourceY} C${(targetX + sourceX) / 2},${sourceY} ${(targetX + sourceX) / 2},${targetY} ${targetX},${targetY}`;
      }
    },

    updateViewport(nodes) {
      const xExtent = D3.extent(nodes, (d) => d.x);
      const yExtent = D3.extent(nodes, (d) => d.y);
      const contentWidth = xExtent[1] - xExtent[0];
      const maxHeight = D3.max(nodes, (d) => d.data.dynamicHeight || Node.height);
      const contentHeight = yExtent[1] - yExtent[0] + maxHeight * 0.4;
      const deviceWidth = window.innerWidth;
      let padding;
      if (deviceWidth < 768) {
        padding = 20;
      } else if (deviceWidth < 1024) {
        padding = 100;
      } else {
        padding = 150;
      }
      const viewWidth = contentWidth + padding * 2.5;
      const viewHeight = contentHeight + padding * 1;
      const centerX = (xExtent[0] + xExtent[1]) / 2;
      const centerY = (yExtent[0] + yExtent[1]) / 2;
      this.$svg
        .transition()
        .duration(TransitionDuration)
        .attr(
          "viewBox",
          `${centerX - viewWidth / 2} ${centerY - viewHeight / 2} ${viewWidth} ${viewHeight}`
        );
    },

    constrainTransform(transform) {
      const svg = this.$svg.node();
      const content = this.$wrap.node();
      const contentBounds = content.getBBox();
      const svgWidth = svg.clientWidth;
      const svgHeight = svg.clientHeight;
      const scaledWidth = contentBounds.width * transform.k;
      const scaledHeight = contentBounds.height * transform.k;
      const maxX = Math.max(0, (scaledWidth - svgWidth) / 2);
      const minX = -maxX;
      const maxY = Math.max(0, (scaledHeight - svgHeight) / 2);
      const minY = -maxY;
      const allowPanX = scaledWidth > svgWidth;
      const allowPanY = scaledHeight > svgHeight;
      transform.x = allowPanX ? Math.max(minX, Math.min(transform.x, maxX)) : transform.x;
      transform.y = allowPanY ? Math.max(minY, Math.min(transform.y, maxY)) : transform.y;
      return transform;
    },

    // ========== 绘制 ==========
    draw(init = false) {
      const isCenterLayout = this.layoutMode === "center";
      let root = this.tree(this.hierarchy);
      root.x = 0;
      root.y = 0;
      let nodes = root.descendants();
      this.nodes = nodes;
      nodes.forEach((a) => {
        [a.x, a.y] = [a.y, a.x];
      });

      if (isCenterLayout) {
        let kids = root.children;
        if (kids.length < 3) {
          let middleOffset = (kids[0].y + kids[kids.length - 1].y) / 2;
          kids.forEach((a) => {
            a.descendants().forEach((b) => {
              b.y -= middleOffset;
            });
          });
        } else {
          let splitIndex = Math.ceil(kids.length / 2);
          let left = kids.slice(0, splitIndex);
          let right = kids.slice(splitIndex);
          let leftMiddleOffset = (left[0].y + left[left.length - 1].y) / 2;
          left.forEach((a) => {
            a.descendants().forEach((b) => {
              b.x = -b.x - this.getWidth(b) / 2;
              b.y -= leftMiddleOffset;
            });
          });
          let rightMiddleOffset = (right[0].y + right[right.length - 1].y) / 2;
          right.forEach((a) => {
            a.descendants().forEach((b) => {
              b.x = b.x + this.getWidth(b) / 2;
              b.y -= rightMiddleOffset;
            });
          });
        }
      } else {
        const maxNodeWidth = D3.max(nodes, (d) => this.getWidth(d)) || 100;
        const horizontalGap = maxNodeWidth + 100;
        const verticalSpacing = 70;
        const depthMap = new Map();
        nodes.forEach((d) => {
          if (!depthMap.has(d.depth)) depthMap.set(d.depth, []);
          depthMap.get(d.depth).push(d);
        });
        depthMap.forEach((group, depth) => {
          const count = group.length;
          group.forEach((d, i) => {
            d.y = (i - (count - 1) / 2) * verticalSpacing;
          });
        });
        nodes.forEach((d) => {
          d.x = d.depth * horizontalGap;
        });
        const rootWidth = this.getWidth(root);
        const rootLeftPadding = 50;
        const offsetX = rootLeftPadding + rootWidth / 2 - root.x;
        nodes.forEach((d) => {
          d.x += offsetX;
        });
      }

      setTimeout(() => {
        this.updateViewport(nodes);
      }, 100);

      let _this = this;
      let $nodes = this.$nodeGroup
        .selectAll(".node")
        .data(nodes, (d) => d.id)
        .join(
          (enter) => {
            let $gs = enter.append("g");
            $gs
              .append("text")
              .attr("fill", "yellowgreen")
              .attr("class", "toggle")
              .attr("dy", 30)
              .attr("x", (d) => {
                if (d.depth === 0) return;
                if (this.layoutMode === "lr") {
                  return this.getWidth(d) / 2 + 10;
                } else {
                  const rectWidth = this.getWidth(d);
                  if (d.x === 0 && d.data.image) {
                    return rectWidth / 2 + 75;
                  } else if (d.x === 0) {
                    return rectWidth / 2 + 10;
                  } else if (d.x > root.x) {
                    if (d.data.image) {
                      return rectWidth / 2 + 65;
                    } else {
                      return rectWidth / 2 + 3;
                    }
                  } else {
                    if (d.data.image) {
                      return -rectWidth / 2 - 80;
                    } else {
                      return -rectWidth / 2 - 22;
                    }
                  }
                }
              })
              .style("cursor", "pointer")
              .style("font-weight", "bolder")
              .style("font-size", "34PX")
              .text((d) => {
                if (d.depth === 0) return;
                if (d.children || d._children) {
                  return d.isFold ? "+" : "−";
                }
              })
              .on("click", this.toggleNode);

            $gs
              .append("foreignObject")
              .attr("x", (d) => -this.getWidth(d) / 2)
              .attr("y", -Node.height * 0.62)
              .attr("width", (d) => this.getWidth(d))
              .attr("height", "150")
              .append("xhtml:div")
              .style("max-width", (d) => this.getWidth(d) + "PX")
              .style("word-break", "break-word")
              .html((d) => md.render(d.data.name))
              .style("pointer-events", "auto")
              .on("click", function (d) {
                _this.onNodeClick(d);
              })
              .style("text-align", "center")
              .style("font-size", "22PX")
              .style("color", Node.color)
              .selectAll("p")
              .attr("class", "node-name")
              .each(function (d) {
                D3.select(this).datum(d);
                D3.select(this)
                  .selectAll("p")
                  .data([d])
                  .attr("class", "node-name")
                  .each(function (d, i) {
                    d.data.nameHeight = this.getBoundingClientRect().height;
                  });
              });

            let descForeign = $gs
              .append("foreignObject")
              .attr("class", "desc-foreign")
              .attr("y", (d) => {
                return d.data.nameHeight || Node.height * 0.56;
              })
              .attr("x", (d) => {
                const isLeftNode = d.x < root.x;
                const nodeWidth = this.getWidth(d);
                if (isLeftNode) {
                  return -Math.max(nodeWidth / 2, 150);
                } else {
                  return -nodeWidth / 2;
                }
              })
              .attr("width", (d) => Math.max(this.getWidth(d), 300))
              .attr("height", 500)
              .style("display", "none");

            descForeign
              .append("xhtml:div")
              .attr("class", "desc-text")
              .html((d) => {
                return md.render(d.data.description ? d.data.description : "");
              })
              .style("text-align", "left")
              .style("padding", "5px");

            $gs
              .append("image")
              .attr("class", "node-image")
              .attr("xlink:href", (d) => getImageSrcFromHtml(md.render(d.data.image)))
              .attr("x", (d) => {
                let isLeftNode = d.x < root.x;
                if (isLeftNode) {
                  return -this.getWidth(d) / 2 - 50;
                } else {
                  return -this.getWidth(d) / 2 + this.getWidth(d) + 10;
                }
              })
              .attr("y", -8)
              .attr("width", 50)
              .attr("height", 50)
              .attr("display", "none")
              .on("click", function (d) {
                _this.onNodeImageClick(d);
              });
            // ★ 学习状态标记点：黄=已学习，绿=已掌握（诊断完成）
            $gs
              .append("circle")
              .attr("class", "status-dot")
              .attr("r", 7)
              .attr("cx", (d) => this.getDotX(d))
              .attr("cy", (d) => this.getDotY(d))
              .attr("display", "none");
            $gs.append("text")
              .attr("class", "game-mark")
              .attr("dy", 30)
              .attr("dx", 4)                    // 相对定位由 x 属性控制
              .attr("x", (d) => (this.getDotX(d) || 0) + 10)   // 排在学习状态点右侧
              .attr("y", 12)
              .style("font-size", "18px")
              .style("cursor", "default")
              .attr("display", "none");
            return $gs;
          },
          (update) => update,
          (exit) => {
            exit
              .transition()
              .duration(init ? 0 : TransitionDuration)
              .attr("opacity", 0)
              .attr("transform", (d) => `translate(${d.parent.x},${d.parent.y})`)
              .remove();
          }
        )
        .attr("class", "node");

      this.$nodeGroup.selectAll(".toggle").attr("x", (d) => {
        if (d.depth === 0) return;
        if (this.layoutMode === "lr") {
          return this.getWidth(d) / 2 + 10;
        } else {
          const rectWidth = this.getWidth(d);
          if (d.x === 0 && d.data.image) {
            return rectWidth / 2 + 75;
          } else if (d.x === 0) {
            return rectWidth / 2 + 10;
          } else if (d.x > root.x) {
            if (d.data.image) {
              return rectWidth / 2 + 65;
            } else {
              return rectWidth / 2 + 3;
            }
          } else {
            if (d.data.image) {
              return -rectWidth / 2 - 80;
            } else {
              return -rectWidth / 2 - 22;
            }
          }
        }
      });

      $nodes.each(function (d) {
        const g = D3.select(this);
        const hasDesc = d.data.description && d.data.description.trim() !== "";
        g.on("mouseenter.desc", null).on("mouseleave.desc", null);
        if (isCenterLayout) {
          g.select(".desc-foreign").style("display", hasDesc ? null : "none");
          g.select(".node-image").style("display", d.data.image ? null : "none");
        } else {
          g.select(".desc-foreign").style("display", "none");
          g.select(".node-image").style("display", "none");
          if (hasDesc) {
            g.on("mouseenter.desc", function () {
              D3.select(this).select(".desc-foreign").style("display", null);
            });
            g.on("mouseleave.desc", function () {
              D3.select(this).select(".desc-foreign").style("display", "none");
            });
          }
        }
      });

      $nodes
        .filter((a) => a.originX !== undefined && a.originY !== undefined)
        .attr("opacity", 0)
        .attr("width", (d) => this.getWidth(d))
        .attr("transform", (d) => {
          let x, y;
          if (d.originX) {
            x = d.originX;
            delete d.originX;
          } else {
            x = d.x;
          }
          if (d.originY) {
            y = d.originY;
            delete d.originY;
          } else {
            y = d.y;
          }
          return `translate(${x}, ${y})`;
        });

      $nodes
        .transition()
        .duration(init ? 0 : TransitionDuration)
        .attr("opacity", 1)
        .attr("transform", (d) => `translate(${d.x}, ${d.y})`)
        .each((d, i, ns) => {
          this.updateNodeHeight(d, ns[i]);
        });

      let links = root.links();
      const linkUpdate = this.$linkGroup.selectAll(".link").data(links, (d) => nanoid());
      const linkEnter = linkUpdate
        .enter()
        .append("path")
        .attr("class", "link")
        .attr("id", (d) => nanoid())
        .attr("fill", "none")
        .style("stroke", (d, i) => interpolateRainbow(i / 100))
        .attr("d", (d) => this.generateConsolidatedPath(d, true));

      linkUpdate
        .merge(linkEnter)
        .transition()
        .duration(init ? 0 : TransitionDuration)
        .attr("d", (d) => this.generateConsolidatedPath(d));

      linkUpdate.exit().remove();
      this.$nextTick(() => {
        this.updateNodeHeights();
        this.adjustDescriptions();
      });
      // ★ 绘制后同步一次节点状态标记（若有注册的状态查询函数）
      if (typeof this.getNodeStatusForNode === 'function') {
        this.refreshNodeStatusMarks();
        this.refreshNodeGameMarks();
      }
    },

    updateNodeHeights() {
      this.$nodeGroup.selectAll(".node-name").each(function () {
        const d = D3.select(this).datum();
        if (d) {
          d.data.nameHeight = this.getBoundingClientRect().height;
        }
      });
    },

    adjustDescriptions() {
      this.$nodeGroup.selectAll(".desc-text").each(function () {
        const d = D3.select(this).datum();
        if (d && d.data.nameHeight) {
          const foreignObject = this.closest("foreignObject");
          if (foreignObject) {
            foreignObject.setAttribute(
              "y",
              (d.data.nameHeight || Node.height * 0.62) + 15
            );
          }
        }
      });
    },

    updateNodeHeight(d, element) {
      const computedHeight = element.getBoundingClientRect().height;
      d.data.dynamicHeight = Math.max(computedHeight, Node.height);
    },

    // ========== 交互 ==========
    toggleNode(d) {
      d.isFold = !d.isFold;
      this.$nodeGroup
        .selectAll(".node .toggle")
        .text((n) => {
          if (n.depth === 0) return;
          if (n.children || n._children) {
            return n.isFold ? "+" : "−";
          }
        });
      if (d.depth !== 0) {
        if (d.children) {
          d._children = d.children;
          d.children = undefined;
        } else if (d._children) {
          d._children.forEach((a) => {
            a.x0 = d.x;
            a.y0 = d.y;
          });
          d.children = d._children;
          d._children = undefined;
        }
        this.draw();
      }
    },
    // ========== ★ 节点学习状态标记 ==========
    // 组件通过 getNodeStatusForNode(d) 提供 'learned' | 'mastered' | 'not'，
    // mixin 只负责把状态画到节点上（点位于节点右上角）
    refreshNodeStatusMarks() {
      if (!this.$nodeGroup) return;
      this.$nodeGroup.selectAll(".node").each((d, i, ns) => {
        const dot = D3.select(ns[i]).select(".status-dot");
        if (dot.empty()) return;
        const status = this.getNodeStatusForNode?.(d) || 'not';
        dot.attr("cx", this.getDotX(d)).attr("cy", this.getDotY(d));
        if (status === 'not') {
          dot.attr("display", "none");
        } else {
          dot.attr("display", null)
             .attr("class", "status-dot " + (status === 'mastered' ? 'is-mastered' : 'is-learned'));
        }
      });
    },

    refreshNodeGameMarks() {
      if (!this.$nodeGroup) return;
      this.$nodeGroup.selectAll(".node").each((d, i, ns) => {
        const mark = D3.select(ns[i]).select(".game-mark");
        if (mark.empty()) return;
        const st = this.getNodeGameStatusForNode?.(d) || '';
        mark.attr("x", (this.getDotX(d) || 0) + 20).attr("display", st ? null : "none");
        if (st === 'game_success') mark.text('👑').attr('fill', '#f5a623');
        else if (st === 'game_fail') mark.text('✕').attr('fill', '#c0c4cc');
        else mark.text('');
      });
    },
    
    resetView() {
      const initialTransform = D3.zoomIdentity.translate(0, 0).scale(1);
      this.$svg
        .transition()
        .duration(500)
        .call(this.zoom.transform, initialTransform);
      this.updateViewport(this.hierarchy.descendants());
    },

    toggleGraphLayout() {
      this.layoutMode = this.layoutMode === "center" ? "lr" : "center";
      this.$nextTick(() => {
        this.draw(false);
      });
    },
    getNodeGameStatusForNode(d) {
      const name = d?.data?.name;
      if (!name || !this.currentKgId) return '';
      try {
        return getNodeGameStatus(this.currentKgId, this.currentLevel, name);
      } catch (e) {
        console.warn('[graphRender] 闯关状态查询失败:', e);
        return '';
      }
    },
  },

};
